/* Copyright (c) 2024 MOCA Systems, Inc.  All rights reserved. */
import { DisplayObject, EventSystem, Point } from 'pixi.js-legacy';
import * as hammer from 'hammerjs';
import { FancyEventBoundary } from '@appCanvasCommon/interaction/FancyEventBoundary';

const config = {
	useOnlyFirstHitTest: true,
	hammerInputEventName: `hammer.input`,
};

//HammerPoint -> Pixi Point
interface PointLike {
	x: number;
	y: number;
}
/**
 * Connects A PIXI.JS V7 App with HammerJS for gesture handling
 * @param appView {HTMLCanvasElement} The canvas element that you want gestures for
 * @param interactionManager {EventSystem|InteractionManager} A PIXI V5 or V6 InteractionManager OR a PIXI V7 EventSystem
 * @param rootDisplayObject {DisplayObject} Required for PIXI V7. A DisplayObject that represents the root EventBoundary for interaction detection
 * @param preInitedHammer {Hammer.Manager} An instance of a pre-initialized Hammer.Manager. Leave null for a new one to be created
 * @constructor
 */
export class HammerConnector {
	private _hammerManager: HammerManager;
	private _rootBoundary: FancyEventBoundary;

	constructor(
		private view: HTMLCanvasElement,
		private eventSystem: EventSystem,
		private rootDisplayObject: DisplayObject,
		hammerManager?: HammerManager
	) {
		this.initializeHammer(hammerManager);
		this._rootBoundary = new FancyEventBoundary(rootDisplayObject, eventSystem);
	}
	/**
	 * Gets the managed Hammer Manager instance
	 * @returns {Hammer.Manager}
	 */
	get manager(): HammerManager {
		return this._hammerManager;
	}
	/**
	 * Connects a list of HammerJS events to PIXI. When a tracked HammerJS event is emitted, it will propagate the event to the PIXI display object that was hit
	 * @param eventTypes {string[]}
	 */
	public registerHandlerTypes(eventTypes: string[]): void {
		let firstTarget: DisplayObject;

		this.manager.on(config.hammerInputEventName, (hammerEvent) => {
			if (hammerEvent.isFirst) {
				firstTarget = this.getTargetDisplayObject(hammerEvent.center);
			}
		});

		eventTypes.forEach((eventName: string) => {
			this.manager.on(eventName, (event) => {
				const target = config.useOnlyFirstHitTest ? firstTarget : this.getTargetDisplayObject(event.center);

				if (target) {
					this.dispatchEvent(target, eventName, event);
				}
			});
		});
	}
	/**
	 * Removes all listeners and clears objects.
	 */
	public destroy(): void {
		for (const key in (this._hammerManager as any).handlers) {
			this._hammerManager.off(key, (this._hammerManager as any).handlers[key]);
		}

		this._hammerManager.destroy();
		this._hammerManager = null;
	}
	/**
	 * Maps a position from the HTMLCanvasElement to a PIXI.Point that corresponds to that location on the Scene Graph
	 * @param dstPoint
	 * @returns {Point}
	 */
	public normalize(destinationPoint: PointLike): Point {
		const point = new Point();
		this.eventSystem.mapPositionToPoint(point, destinationPoint.x, destinationPoint.y);
		return point;
	}
	/**
	 * Perform a `hitTest` on the Scene Graph and return the first DisplayObject hit by the interaction event
	 * @param center {PIXI.Point}
	 * @returns {DisplayObject}
	 */
	public getTargetDisplayObject(center: HammerPoint): DisplayObject {
		const normalizedCenter: Point = this.normalize(center);
		return this._rootBoundary.hitTest(normalizedCenter.x, normalizedCenter.y);
	}
	/**
	 * Dispatches the hammer-* event on the DisplayObject when it is interacted with
	 * When used with PIXI V7, the event will be dispatched via the DisplayObject's EventEmitter `emit`.
	 * This is because PIXI V7 dispatchEvent method disallows any event that does not conform to its FederatedEvent class
	 * @param pixiDisplayObject {DisplayObject} The DisplayObject that was hit by this Interaction
	 * @param eventName {string} The hammer-* event name that represents the gesture
	 * @param event {Event} The HammerJS event containing gesture data
	 */
	public dispatchEvent(displayObject: DisplayObject, eventName: string, event: HammerInput): void {
		displayObject.emit(this.decorate(eventName), event);
	}

	/**
	 * Initializes the Hammer Wrapper.
	 * This will be a previously initialized hammer.Manager (through the HammerConnector constructor), otherwise it will be a new instance
	 * @param hammerManager
	 */
	private initializeHammer(hammerManager?: HammerManager): void {
		if (hammerManager) {
			this._hammerManager = hammerManager;
			return;
		}

		this._hammerManager = new hammer.Manager(this.view);
	}
	/**
	 * Just prefix everything hammer-y with hammer- to avoid conflicts
	 * @param  {[string]} eventName
	 * @return {[string]}
	 */
	private decorate(eventName: string): string {
		return `hammer-${eventName}`;
	}
}
